import { z } from 'zod';

/**
 * Länderliste (ISO 3166-1 alpha-2) — die EINZIGE Quelle für Länder in ProERP
 * (Tech-Lead 2026-09-29, ADR 0109: „Land wird aus einer Liste gewählt, ISO-Kürzel
 * im Hintergrund"). Aus ihr speisen sich:
 *
 * - der Länder-Picker im CRM (Kunde, Lieferadresse),
 * - die Steuerfindung (`normalizeCountryCode` → `determineTax`, ADR 0029),
 * - der PDF-Druck (Kürzel → Name in der Belegsprache, `countryLabel`),
 * - die Zuordnungs-Migration `customer_country_iso` (Alias-Tabelle, siehe
 *   `tools/country-migration-sql.ts`).
 *
 * Vorher war das Land im Kundenstamm Freitext; „Niederlande" wurde auf „NI"
 * gekürzt und als Drittland behandelt (Qualitäts-Audit 2026-09-28). Die
 * früheren Schreibweisen aus der Steuerfindung (`COUNTRY_NAMES`) und dem
 * Altdaten-Import stecken hier als `aliases`.
 *
 * Quelle: ISO 3166-1 (Debian `iso-codes`) plus Kosovo (XK, EU-Praxis); Namen
 * aus CLDR (Deutsch/Englisch), wenige Kurzformen („Hongkong" statt
 * „Sonderverwaltungsregion Hongkong", „and" statt „&").
 */

/** `[ISO-2, ISO-3, Deutsch, Englisch, weitere Schreibweisen?]` */
type CountryRow = readonly [code: string, alpha3: string, nameDe: string, nameEn: string, aliases?: readonly string[]];

const COUNTRY_ROWS: readonly CountryRow[] = [
  ['AD', 'AND', 'Andorra', 'Andorra'],
  ['AE', 'ARE', 'Vereinigte Arabische Emirate', 'United Arab Emirates', ['VAE', 'UAE']],
  ['AF', 'AFG', 'Afghanistan', 'Afghanistan'],
  ['AG', 'ATG', 'Antigua und Barbuda', 'Antigua and Barbuda'],
  ['AI', 'AIA', 'Anguilla', 'Anguilla'],
  ['AL', 'ALB', 'Albanien', 'Albania'],
  ['AM', 'ARM', 'Armenien', 'Armenia'],
  ['AO', 'AGO', 'Angola', 'Angola'],
  ['AQ', 'ATA', 'Antarktis', 'Antarctica'],
  ['AR', 'ARG', 'Argentinien', 'Argentina'],
  ['AS', 'ASM', 'Amerikanisch-Samoa', 'American Samoa'],
  ['AT', 'AUT', 'Österreich', 'Austria'],
  ['AU', 'AUS', 'Australien', 'Australia'],
  ['AW', 'ABW', 'Aruba', 'Aruba'],
  ['AX', 'ALA', 'Ålandinseln', 'Åland Islands'],
  ['AZ', 'AZE', 'Aserbaidschan', 'Azerbaijan'],
  ['BA', 'BIH', 'Bosnien und Herzegowina', 'Bosnia and Herzegovina'],
  ['BB', 'BRB', 'Barbados', 'Barbados'],
  ['BD', 'BGD', 'Bangladesch', 'Bangladesh'],
  ['BE', 'BEL', 'Belgien', 'Belgium'],
  ['BF', 'BFA', 'Burkina Faso', 'Burkina Faso'],
  ['BG', 'BGR', 'Bulgarien', 'Bulgaria'],
  ['BH', 'BHR', 'Bahrain', 'Bahrain'],
  ['BI', 'BDI', 'Burundi', 'Burundi'],
  ['BJ', 'BEN', 'Benin', 'Benin'],
  ['BL', 'BLM', 'St. Barthélemy', 'St. Barthélemy'],
  ['BM', 'BMU', 'Bermuda', 'Bermuda'],
  ['BN', 'BRN', 'Brunei Darussalam', 'Brunei'],
  ['BO', 'BOL', 'Bolivien', 'Bolivia'],
  ['BQ', 'BES', 'Karibische Niederlande', 'Caribbean Netherlands'],
  ['BR', 'BRA', 'Brasilien', 'Brazil'],
  ['BS', 'BHS', 'Bahamas', 'Bahamas'],
  ['BT', 'BTN', 'Bhutan', 'Bhutan'],
  ['BV', 'BVT', 'Bouvetinsel', 'Bouvet Island'],
  ['BW', 'BWA', 'Botsuana', 'Botswana'],
  ['BY', 'BLR', 'Belarus', 'Belarus', ['Weißrussland']],
  ['BZ', 'BLZ', 'Belize', 'Belize'],
  ['CA', 'CAN', 'Kanada', 'Canada'],
  ['CC', 'CCK', 'Kokosinseln', 'Cocos (Keeling) Islands'],
  ['CD', 'COD', 'Kongo-Kinshasa', 'Congo - Kinshasa', ['Demokratische Republik Kongo', 'DR Kongo', 'DR Congo']],
  ['CF', 'CAF', 'Zentralafrikanische Republik', 'Central African Republic'],
  ['CG', 'COG', 'Kongo-Brazzaville', 'Congo - Brazzaville', ['Republik Kongo', 'Republic of the Congo']],
  ['CH', 'CHE', 'Schweiz', 'Switzerland'],
  ['CI', 'CIV', 'Côte d’Ivoire', 'Côte d’Ivoire', ['Elfenbeinküste', 'Ivory Coast']],
  ['CK', 'COK', 'Cookinseln', 'Cook Islands'],
  ['CL', 'CHL', 'Chile', 'Chile'],
  ['CM', 'CMR', 'Kamerun', 'Cameroon'],
  ['CN', 'CHN', 'China', 'China', ['Volksrepublik China', 'VR China']],
  ['CO', 'COL', 'Kolumbien', 'Colombia'],
  ['CR', 'CRI', 'Costa Rica', 'Costa Rica'],
  ['CU', 'CUB', 'Kuba', 'Cuba'],
  ['CV', 'CPV', 'Cabo Verde', 'Cape Verde', ['Kap Verde']],
  ['CW', 'CUW', 'Curaçao', 'Curaçao'],
  ['CX', 'CXR', 'Weihnachtsinsel', 'Christmas Island'],
  ['CY', 'CYP', 'Zypern', 'Cyprus'],
  ['CZ', 'CZE', 'Tschechien', 'Czechia', ['Tschechische Republik', 'Czech Republic']],
  ['DE', 'DEU', 'Deutschland', 'Germany', ['BRD', 'Bundesrepublik Deutschland']],
  ['DJ', 'DJI', 'Dschibuti', 'Djibouti'],
  ['DK', 'DNK', 'Dänemark', 'Denmark'],
  ['DM', 'DMA', 'Dominica', 'Dominica'],
  ['DO', 'DOM', 'Dominikanische Republik', 'Dominican Republic'],
  ['DZ', 'DZA', 'Algerien', 'Algeria'],
  ['EC', 'ECU', 'Ecuador', 'Ecuador'],
  ['EE', 'EST', 'Estland', 'Estonia'],
  ['EG', 'EGY', 'Ägypten', 'Egypt'],
  ['EH', 'ESH', 'Westsahara', 'Western Sahara'],
  ['ER', 'ERI', 'Eritrea', 'Eritrea'],
  ['ES', 'ESP', 'Spanien', 'Spain'],
  ['ET', 'ETH', 'Äthiopien', 'Ethiopia'],
  ['FI', 'FIN', 'Finnland', 'Finland'],
  ['FJ', 'FJI', 'Fidschi', 'Fiji'],
  ['FK', 'FLK', 'Falklandinseln', 'Falkland Islands'],
  ['FM', 'FSM', 'Mikronesien', 'Micronesia'],
  ['FO', 'FRO', 'Färöer', 'Faroe Islands'],
  ['FR', 'FRA', 'Frankreich', 'France'],
  ['GA', 'GAB', 'Gabun', 'Gabon'],
  ['GB', 'GBR', 'Vereinigtes Königreich', 'United Kingdom', ['Großbritannien', 'England', 'Great Britain', 'UK', 'Schottland', 'Scotland', 'Wales', 'Nordirland', 'Northern Ireland']],
  ['GD', 'GRD', 'Grenada', 'Grenada'],
  ['GE', 'GEO', 'Georgien', 'Georgia'],
  ['GF', 'GUF', 'Französisch-Guayana', 'French Guiana'],
  ['GG', 'GGY', 'Guernsey', 'Guernsey'],
  ['GH', 'GHA', 'Ghana', 'Ghana'],
  ['GI', 'GIB', 'Gibraltar', 'Gibraltar'],
  ['GL', 'GRL', 'Grönland', 'Greenland'],
  ['GM', 'GMB', 'Gambia', 'Gambia'],
  ['GN', 'GIN', 'Guinea', 'Guinea'],
  ['GP', 'GLP', 'Guadeloupe', 'Guadeloupe'],
  ['GQ', 'GNQ', 'Äquatorialguinea', 'Equatorial Guinea'],
  ['GR', 'GRC', 'Griechenland', 'Greece'],
  ['GS', 'SGS', 'Südgeorgien und die Südlichen Sandwichinseln', 'South Georgia and South Sandwich Islands'],
  ['GT', 'GTM', 'Guatemala', 'Guatemala'],
  ['GU', 'GUM', 'Guam', 'Guam'],
  ['GW', 'GNB', 'Guinea-Bissau', 'Guinea-Bissau'],
  ['GY', 'GUY', 'Guyana', 'Guyana'],
  ['HK', 'HKG', 'Hongkong', 'Hong Kong'],
  ['HM', 'HMD', 'Heard und McDonaldinseln', 'Heard and McDonald Islands'],
  ['HN', 'HND', 'Honduras', 'Honduras'],
  ['HR', 'HRV', 'Kroatien', 'Croatia'],
  ['HT', 'HTI', 'Haiti', 'Haiti'],
  ['HU', 'HUN', 'Ungarn', 'Hungary'],
  ['ID', 'IDN', 'Indonesien', 'Indonesia'],
  ['IE', 'IRL', 'Irland', 'Ireland'],
  ['IL', 'ISR', 'Israel', 'Israel'],
  ['IM', 'IMN', 'Isle of Man', 'Isle of Man'],
  ['IN', 'IND', 'Indien', 'India'],
  ['IO', 'IOT', 'Britisches Territorium im Indischen Ozean', 'British Indian Ocean Territory'],
  ['IQ', 'IRQ', 'Irak', 'Iraq'],
  ['IR', 'IRN', 'Iran', 'Iran'],
  ['IS', 'ISL', 'Island', 'Iceland'],
  ['IT', 'ITA', 'Italien', 'Italy'],
  ['JE', 'JEY', 'Jersey', 'Jersey'],
  ['JM', 'JAM', 'Jamaika', 'Jamaica'],
  ['JO', 'JOR', 'Jordanien', 'Jordan'],
  ['JP', 'JPN', 'Japan', 'Japan'],
  ['KE', 'KEN', 'Kenia', 'Kenya'],
  ['KG', 'KGZ', 'Kirgisistan', 'Kyrgyzstan'],
  ['KH', 'KHM', 'Kambodscha', 'Cambodia'],
  ['KI', 'KIR', 'Kiribati', 'Kiribati'],
  ['KM', 'COM', 'Komoren', 'Comoros'],
  ['KN', 'KNA', 'St. Kitts und Nevis', 'St. Kitts and Nevis'],
  ['KP', 'PRK', 'Nordkorea', 'North Korea'],
  ['KR', 'KOR', 'Südkorea', 'South Korea', ['Korea', 'Republik Korea', 'Republic of Korea']],
  ['KW', 'KWT', 'Kuwait', 'Kuwait'],
  ['KY', 'CYM', 'Kaimaninseln', 'Cayman Islands'],
  ['KZ', 'KAZ', 'Kasachstan', 'Kazakhstan'],
  ['LA', 'LAO', 'Laos', 'Laos'],
  ['LB', 'LBN', 'Libanon', 'Lebanon'],
  ['LC', 'LCA', 'St. Lucia', 'St. Lucia'],
  ['LI', 'LIE', 'Liechtenstein', 'Liechtenstein'],
  ['LK', 'LKA', 'Sri Lanka', 'Sri Lanka'],
  ['LR', 'LBR', 'Liberia', 'Liberia'],
  ['LS', 'LSO', 'Lesotho', 'Lesotho'],
  ['LT', 'LTU', 'Litauen', 'Lithuania'],
  ['LU', 'LUX', 'Luxemburg', 'Luxembourg'],
  ['LV', 'LVA', 'Lettland', 'Latvia'],
  ['LY', 'LBY', 'Libyen', 'Libya'],
  ['MA', 'MAR', 'Marokko', 'Morocco'],
  ['MC', 'MCO', 'Monaco', 'Monaco'],
  ['MD', 'MDA', 'Republik Moldau', 'Moldova', ['Moldawien', 'Moldau']],
  ['ME', 'MNE', 'Montenegro', 'Montenegro'],
  ['MF', 'MAF', 'St. Martin', 'St. Martin'],
  ['MG', 'MDG', 'Madagaskar', 'Madagascar'],
  ['MH', 'MHL', 'Marshallinseln', 'Marshall Islands'],
  ['MK', 'MKD', 'Nordmazedonien', 'North Macedonia', ['Mazedonien', 'Macedonia']],
  ['ML', 'MLI', 'Mali', 'Mali'],
  ['MM', 'MMR', 'Myanmar', 'Myanmar', ['Burma', 'Birma']],
  ['MN', 'MNG', 'Mongolei', 'Mongolia'],
  ['MO', 'MAC', 'Macau', 'Macao'],
  ['MP', 'MNP', 'Nördliche Marianen', 'Northern Mariana Islands'],
  ['MQ', 'MTQ', 'Martinique', 'Martinique'],
  ['MR', 'MRT', 'Mauretanien', 'Mauritania'],
  ['MS', 'MSR', 'Montserrat', 'Montserrat'],
  ['MT', 'MLT', 'Malta', 'Malta'],
  ['MU', 'MUS', 'Mauritius', 'Mauritius'],
  ['MV', 'MDV', 'Malediven', 'Maldives'],
  ['MW', 'MWI', 'Malawi', 'Malawi'],
  ['MX', 'MEX', 'Mexiko', 'Mexico'],
  ['MY', 'MYS', 'Malaysia', 'Malaysia'],
  ['MZ', 'MOZ', 'Mosambik', 'Mozambique'],
  ['NA', 'NAM', 'Namibia', 'Namibia'],
  ['NC', 'NCL', 'Neukaledonien', 'New Caledonia'],
  ['NE', 'NER', 'Niger', 'Niger'],
  ['NF', 'NFK', 'Norfolkinsel', 'Norfolk Island'],
  ['NG', 'NGA', 'Nigeria', 'Nigeria'],
  ['NI', 'NIC', 'Nicaragua', 'Nicaragua'],
  ['NL', 'NLD', 'Niederlande', 'Netherlands', ['Holland', 'The Netherlands']],
  ['NO', 'NOR', 'Norwegen', 'Norway'],
  ['NP', 'NPL', 'Nepal', 'Nepal'],
  ['NR', 'NRU', 'Nauru', 'Nauru'],
  ['NU', 'NIU', 'Niue', 'Niue'],
  ['NZ', 'NZL', 'Neuseeland', 'New Zealand'],
  ['OM', 'OMN', 'Oman', 'Oman'],
  ['PA', 'PAN', 'Panama', 'Panama'],
  ['PE', 'PER', 'Peru', 'Peru'],
  ['PF', 'PYF', 'Französisch-Polynesien', 'French Polynesia'],
  ['PG', 'PNG', 'Papua-Neuguinea', 'Papua New Guinea'],
  ['PH', 'PHL', 'Philippinen', 'Philippines'],
  ['PK', 'PAK', 'Pakistan', 'Pakistan'],
  ['PL', 'POL', 'Polen', 'Poland'],
  ['PM', 'SPM', 'St. Pierre und Miquelon', 'St. Pierre and Miquelon'],
  ['PN', 'PCN', 'Pitcairninseln', 'Pitcairn Islands'],
  ['PR', 'PRI', 'Puerto Rico', 'Puerto Rico'],
  ['PS', 'PSE', 'Palästinensische Autonomiegebiete', 'Palestinian Territories'],
  ['PT', 'PRT', 'Portugal', 'Portugal'],
  ['PW', 'PLW', 'Palau', 'Palau'],
  ['PY', 'PRY', 'Paraguay', 'Paraguay'],
  ['QA', 'QAT', 'Katar', 'Qatar'],
  ['RE', 'REU', 'Réunion', 'Réunion'],
  ['RO', 'ROU', 'Rumänien', 'Romania'],
  ['RS', 'SRB', 'Serbien', 'Serbia'],
  ['RU', 'RUS', 'Russland', 'Russia', ['Russische Föderation', 'Russian Federation']],
  ['RW', 'RWA', 'Ruanda', 'Rwanda'],
  ['SA', 'SAU', 'Saudi-Arabien', 'Saudi Arabia'],
  ['SB', 'SLB', 'Salomonen', 'Solomon Islands'],
  ['SC', 'SYC', 'Seychellen', 'Seychelles'],
  ['SD', 'SDN', 'Sudan', 'Sudan'],
  ['SE', 'SWE', 'Schweden', 'Sweden'],
  ['SG', 'SGP', 'Singapur', 'Singapore'],
  ['SH', 'SHN', 'St. Helena', 'St. Helena'],
  ['SI', 'SVN', 'Slowenien', 'Slovenia'],
  ['SJ', 'SJM', 'Spitzbergen und Jan Mayen', 'Svalbard and Jan Mayen'],
  ['SK', 'SVK', 'Slowakei', 'Slovakia'],
  ['SL', 'SLE', 'Sierra Leone', 'Sierra Leone'],
  ['SM', 'SMR', 'San Marino', 'San Marino'],
  ['SN', 'SEN', 'Senegal', 'Senegal'],
  ['SO', 'SOM', 'Somalia', 'Somalia'],
  ['SR', 'SUR', 'Suriname', 'Suriname'],
  ['SS', 'SSD', 'Südsudan', 'South Sudan'],
  ['ST', 'STP', 'São Tomé und Príncipe', 'São Tomé and Príncipe'],
  ['SV', 'SLV', 'El Salvador', 'El Salvador'],
  ['SX', 'SXM', 'Sint Maarten', 'Sint Maarten'],
  ['SY', 'SYR', 'Syrien', 'Syria'],
  ['SZ', 'SWZ', 'Eswatini', 'Eswatini', ['Swasiland', 'Swaziland']],
  ['TC', 'TCA', 'Turks- und Caicosinseln', 'Turks and Caicos Islands'],
  ['TD', 'TCD', 'Tschad', 'Chad'],
  ['TF', 'ATF', 'Französische Süd- und Antarktisgebiete', 'French Southern Territories'],
  ['TG', 'TGO', 'Togo', 'Togo'],
  ['TH', 'THA', 'Thailand', 'Thailand'],
  ['TJ', 'TJK', 'Tadschikistan', 'Tajikistan'],
  ['TK', 'TKL', 'Tokelau', 'Tokelau'],
  ['TL', 'TLS', 'Timor-Leste', 'Timor-Leste'],
  ['TM', 'TKM', 'Turkmenistan', 'Turkmenistan'],
  ['TN', 'TUN', 'Tunesien', 'Tunisia'],
  ['TO', 'TON', 'Tonga', 'Tonga'],
  ['TR', 'TUR', 'Türkei', 'Türkiye', ['Turkey', 'Turkiye']],
  ['TT', 'TTO', 'Trinidad und Tobago', 'Trinidad and Tobago'],
  ['TV', 'TUV', 'Tuvalu', 'Tuvalu'],
  ['TW', 'TWN', 'Taiwan', 'Taiwan'],
  ['TZ', 'TZA', 'Tansania', 'Tanzania'],
  ['UA', 'UKR', 'Ukraine', 'Ukraine'],
  ['UG', 'UGA', 'Uganda', 'Uganda'],
  ['UM', 'UMI', 'Amerikanische Überseeinseln', 'U.S. Outlying Islands'],
  ['US', 'USA', 'Vereinigte Staaten', 'United States', ['Vereinigte Staaten von Amerika', 'United States of America', 'Amerika', 'America']],
  ['UY', 'URY', 'Uruguay', 'Uruguay'],
  ['UZ', 'UZB', 'Usbekistan', 'Uzbekistan'],
  ['VA', 'VAT', 'Vatikanstadt', 'Vatican City', ['Vatikan', 'Vatican']],
  ['VC', 'VCT', 'St. Vincent und die Grenadinen', 'St. Vincent and Grenadines'],
  ['VE', 'VEN', 'Venezuela', 'Venezuela'],
  ['VG', 'VGB', 'Britische Jungferninseln', 'British Virgin Islands'],
  ['VI', 'VIR', 'Amerikanische Jungferninseln', 'U.S. Virgin Islands'],
  ['VN', 'VNM', 'Vietnam', 'Vietnam'],
  ['VU', 'VUT', 'Vanuatu', 'Vanuatu'],
  ['WF', 'WLF', 'Wallis und Futuna', 'Wallis and Futuna'],
  ['WS', 'WSM', 'Samoa', 'Samoa'],
  ['XK', 'XKX', 'Kosovo', 'Kosovo', ['Kosova']],
  ['YE', 'YEM', 'Jemen', 'Yemen'],
  ['YT', 'MYT', 'Mayotte', 'Mayotte'],
  ['ZA', 'ZAF', 'Südafrika', 'South Africa'],
  ['ZM', 'ZMB', 'Sambia', 'Zambia'],
  ['ZW', 'ZWE', 'Simbabwe', 'Zimbabwe'],
];

export type CountryLanguage = 'DE' | 'EN';

export interface Country {
  /** ISO 3166-1 alpha-2, z. B. „NL" — so steht es in `customers.country`. */
  code: string;
  /** ISO 3166-1 alpha-3, z. B. „NLD" (nur als Schreibweise für die Zuordnung). */
  alpha3: string;
  nameDe: string;
  nameEn: string;
  /** Weitere Schreibweisen („Holland", „The Netherlands"). */
  aliases: readonly string[];
}

export const COUNTRIES: readonly Country[] = COUNTRY_ROWS.map(([code, alpha3, nameDe, nameEn, aliases]) => ({
  code,
  alpha3,
  nameDe,
  nameEn,
  aliases: aliases ?? [],
}));

/**
 * Meistgenutzte Länder — stehen im Picker vorn, der Rest folgt alphabetisch.
 * Reihenfolge ist eine fachliche Setzung (Heimatmärkte zuerst), keine Zählung.
 */
export const PREFERRED_COUNTRY_CODES: readonly string[] = [
  'AT', 'DE', 'CH', 'IT', 'NL', 'LI', 'FR', 'BE', 'LU', 'PL', 'CZ', 'SK', 'SI', 'HU', 'HR',
];

const BY_CODE: ReadonlyMap<string, Country> = new Map(COUNTRIES.map((country) => [country.code, country]));

// ── Schlüssel für die Zuordnung von Freitext ─────────────────────────────────
// Die Regeln sind als Konstanten exportiert, weil die Migration
// `customer_country_iso` DIESELBE Funktion in SQL nachbaut (generiert aus diesen
// Werten). Bewusst nur ASCII-Großschreibung — `upper()` in Postgres hängt sonst
// von der Datenbank-Locale ab.

/** Umlaute werden ausgeschrieben („Österreich" = „Oesterreich"). */
export const COUNTRY_KEY_UMLAUTS: ReadonlyArray<readonly [string, string]> = [
  ['ä', 'ae'], ['Ä', 'AE'], ['ö', 'oe'], ['Ö', 'OE'], ['ü', 'ue'], ['Ü', 'UE'], ['ß', 'ss'],
];
/** Akzente fallen weg („Côte" = „Cote"); Zeichen für Zeichen, gleich lang. */
export const COUNTRY_KEY_ACCENTS_FROM = 'ÀÁÂÃÅÇÈÉÊËÌÍÎÏÑÒÓÔÕØÙÚÛÝàáâãåçèéêëìíîïñòóôõøùúûýÿ';
export const COUNTRY_KEY_ACCENTS_TO = 'AAAAACEEEEIIIINOOOOOUUUYaaaaaceeeeiiiinooooouuuyy';
/** Satzzeichen, die ersatzlos wegfallen („St. Lucia" = „St Lucia", „d’Ivoire" = „dIvoire"). */
export const COUNTRY_KEY_DROP = ".'’‘`´()";
/** Trenner, die zu EINEM Leerzeichen werden („Guinea-Bissau" = „Guinea Bissau"). */
export const COUNTRY_KEY_SEPARATORS = ' \t\r\n _–—-';

const SEPARATOR_RUN = /[ \t\r\n _–—-]+/g;

/** Vergleichsschlüssel für Ländernamen: nur A–Z, Ziffern und einfache Leerzeichen. */
export function countryKey(raw: string): string {
  let value = raw;
  for (const [from, to] of COUNTRY_KEY_UMLAUTS) value = value.split(from).join(to);
  value = Array.from(value, (char) => {
    const index = COUNTRY_KEY_ACCENTS_FROM.indexOf(char);
    return index >= 0 ? COUNTRY_KEY_ACCENTS_TO[index] : char;
  }).join('');
  value = value.replace(/[a-z]/g, (char) => char.toUpperCase());
  value = Array.from(value).filter((char) => !COUNTRY_KEY_DROP.includes(char)).join('');
  return value.replace(SEPARATOR_RUN, ' ').replace(/^ +| +$/g, '');
}

/**
 * Alle Schreibweisen als `[Schlüssel, ISO-2]` — Kürzel, ISO-3, deutscher und
 * englischer Name, weitere Schreibweisen. Grundlage für `normalizeCountryCode`
 * und für die Alias-Tabelle der Migration.
 */
export function countryAliasEntries(): Array<readonly [key: string, code: string]> {
  const entries: Array<readonly [string, string]> = [];
  const seen = new Set<string>();
  for (const country of COUNTRIES) {
    for (const spelling of [country.code, country.alpha3, country.nameDe, country.nameEn, ...country.aliases]) {
      const key = countryKey(spelling);
      const id = `${key}\u0000${country.code}`;
      if (!key || seen.has(id)) continue;
      seen.add(id);
      entries.push([key, country.code]);
    }
  }
  return entries;
}

const ALIAS_TO_CODE: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [key, code] of countryAliasEntries()) if (!map.has(key)) map.set(key, code);
  return map;
})();

const trimmedOrNull = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
};

// ── Abfragen ─────────────────────────────────────────────────────────────────

/** Ist das ein Kürzel aus der Liste — exakt so, wie es gespeichert wird („NL")? */
export function isCountryCode(value: string | null | undefined): boolean {
  return value != null && BY_CODE.has(value);
}

/** Kanonisches Kürzel, wenn `value` ein Kürzel der Liste ist („ nl " → „NL"); sonst null. Namen zählen nicht. */
export function countryCodeOf(value: string | null | undefined): string | null {
  const code = (value ?? '').trim().toUpperCase();
  return BY_CODE.has(code) ? code : null;
}

/**
 * Freitext-Land auf ISO-2 normalisieren: deutsche/englische Namen, ISO-2,
 * ISO-3 und bekannte Schreibweisen, groß-/klein-, umlaut- und akzenttolerant.
 * Unbekanntes ergibt '' — nie raten: leer führt in `determineTax` zum sicheren
 * Inlands-Default statt zu einer erfundenen Steuerbefreiung. Auch ein
 * Zwei-Buchstaben-Kürzel zählt nur, wenn es in der Liste steht.
 */
export function normalizeCountryCode(raw: string | null | undefined): string {
  if (!raw) return '';
  return ALIAS_TO_CODE.get(countryKey(raw)) ?? '';
}

/**
 * ISO-Land eines Datensatzes mit den beiden Feldern `country` (Kürzel) und
 * `countryName` (Klartext): das Kürzel hat Vorrang, der Klartext zählt nur,
 * wenn das Kürzel nichts ergibt — dieselbe Regel wie die Migration.
 */
export function resolveCountryCode(country: string | null | undefined, countryName: string | null | undefined): string {
  return normalizeCountryCode(country) || normalizeCountryCode(countryName);
}

/** Name eines Kürzels in der gewünschten Sprache; null, wenn das Kürzel nicht in der Liste steht. */
export function countryLabel(code: string | null | undefined, language: CountryLanguage = 'DE'): string | null {
  const country = BY_CODE.get(countryCodeOf(code) ?? '');
  if (!country) return null;
  return language === 'EN' ? country.nameEn : country.nameDe;
}

/**
 * Anzeigename eines Datensatzes: Name zum Kürzel in der gewünschten Sprache,
 * sonst der gespeicherte Text (nicht zugeordnete Altdaten bleiben lesbar).
 */
export function countryDisplayName(
  country: string | null | undefined,
  countryName: string | null | undefined,
  language: CountryLanguage = 'DE',
): string | null {
  return countryLabel(country, language) ?? trimmedOrNull(countryName) ?? trimmedOrNull(country);
}

/**
 * Letzte Zeile eines Adress-Snapshots (Beleg-Anschrift): das ISO-Kürzel (auch
 * aus eindeutigem Klartext aufgelöst), damit der PDF-Druck den Namen in der
 * Belegsprache einsetzen kann (`expandCountryInAddress`); lässt sich das Land
 * nicht zuordnen, der gespeicherte Text.
 */
export function countryAddressLine(
  country: string | null | undefined,
  countryName: string | null | undefined,
): string | null {
  return resolveCountryCode(country, countryName) || (trimmedOrNull(countryName) ?? trimmedOrNull(country));
}

/**
 * Gespeichertes Land, das sich keinem Eintrag der Liste zuordnen lässt — für
 * den Hinweis am Formularfeld („Land „Holland“ nicht erkannt …") und die
 * Nacharbeitsliste. null, wenn zugeordnet oder leer.
 */
export function unmappedCountryText(
  country: string | null | undefined,
  countryName: string | null | undefined,
): string | null {
  if (resolveCountryCode(country, countryName)) return null;
  return trimmedOrNull(country) ?? trimmedOrNull(countryName);
}

/**
 * Suchbegriffe für einen Land-Filter auf Kunden (CRM-Berichte, Selektionspool,
 * Kundenliste): `exact` für das ISO-Feld — so getippt (Altwerte) und, falls
 * zuordenbar, als Kürzel („Niederlande" → NL, trifft auch Kunden mit „Holland"
 * im Klartext); `contains` als Teiltreffer im Klartext erst ab drei Zeichen —
 * „nl" steckt in „Finnland", „HR" in „Bahrain". EINE Regel für alle Filter.
 */
export function countryFilterTerms(value: string): { exact: string[]; contains: string | null } {
  const trimmed = value.trim();
  const exact = [trimmed];
  const code = normalizeCountryCode(trimmed);
  if (code && code !== trimmed.toUpperCase()) exact.push(code);
  return { exact, contains: trimmed.length > 2 ? trimmed : null };
}

export interface CountryOption {
  code: string;
  label: string;
  /** Gehört zu den meistgenutzten Ländern (steht vorn). */
  isPreferred: boolean;
}

/** Einträge für den Picker: meistgenutzte Länder zuerst, danach alphabetisch nach Name. */
export function countryOptions(language: CountryLanguage = 'DE'): CountryOption[] {
  const labelOf = (country: Country) => (language === 'EN' ? country.nameEn : country.nameDe);
  const preferred = PREFERRED_COUNTRY_CODES.map((code) => BY_CODE.get(code)).filter(
    (country): country is Country => Boolean(country),
  );
  const preferredSet = new Set(PREFERRED_COUNTRY_CODES);
  const rest = COUNTRIES.filter((country) => !preferredSet.has(country.code)).sort((a, b) =>
    labelOf(a).localeCompare(labelOf(b), language === 'EN' ? 'en' : 'de'),
  );
  return [
    ...preferred.map((country) => ({ code: country.code, label: labelOf(country), isPreferred: true })),
    ...rest.map((country) => ({ code: country.code, label: labelOf(country), isPreferred: false })),
  ];
}

/**
 * Zod: Land als ISO-Kürzel aus der Liste (Kleinschreibung wird angeglichen).
 * Namen werden bewusst NICHT angenommen — das Land wird im Picker gewählt.
 */
export const countryCodeSchema = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    if (!BY_CODE.has(value.toUpperCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Land „${value}“ ist kein ISO-Kürzel aus der Länderliste — bitte aus der Liste wählen (z. B. „NL“ für Niederlande).`,
      });
    }
  })
  .transform((value) => value.toUpperCase());
